// Blog.jsx
import { useState, useEffect, useCallback } from "react";
import {
  FiSearch, FiArrowLeft, FiClock,
  FiCalendar, FiTag, FiLayers, FiStar,
  FiAlertTriangle, FiRefreshCw, FiBookOpen,
} from "react-icons/fi";
import {
  HiOutlineUserGroup, HiOutlineHeart, HiOutlineScale,
  HiOutlineLightningBolt, HiOutlineSparkles, HiOutlineGlobe,
} from "react-icons/hi";
import { MdOutlineWorkspaces } from "react-icons/md";
import "../styles/Blog.css";

// ─── Constants ────────────────────────────────────────────────────────────────

const API_BASE_URL = "https://jobs.mpdatahub.com/api";

const CATEGORIES = ["All", "Recruitment", "Culture", "Compliance", "Performance", "Well-being", "DEI"];

const CAT_META = {
  All: { icon: <MdOutlineWorkspaces />, color: "#ac8f57" },
  Recruitment: { icon: <HiOutlineUserGroup />, color: "#ac8f57" },
  Culture: { icon: <HiOutlineHeart />, color: "#c4a882" },
  Compliance: { icon: <HiOutlineScale />, color: "#8a7245" },
  Performance: { icon: <HiOutlineLightningBolt />, color: "#ac8f57" },
  "Well-being": { icon: <HiOutlineSparkles />, color: "#c4a882" },
  DEI: { icon: <HiOutlineGlobe />, color: "#8a7245" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name = "") {
  return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() || "HR";
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CategoryChip({ category, size = "sm" }) {
  return (
    <span className={`cat-chip cat-chip--${size}`} data-category={category}>
      {category}
    </span>
  );
}

function AuthorAvatar({ name }) {
  return <div className="avatar">{getInitials(name)}</div>;
}

// ─── Featured Card ────────────────────────────────────────────────────────────

function FeaturedCard({ blog, onSelect }) {
  return (
    <div className="featured-card" onClick={() => onSelect(blog)}>
      <div className="featured-card__img-wrap">
        <img src={blog.image} alt={blog.title} className="featured-card__img" />
        <div className="featured-card__gradient" />
        <span className="featured-badge">
          <FiStar /> Featured
        </span>
        <div className="featured-card__overlay-content">
          <CategoryChip category={blog.category} size="md" />
          <h2 className="featured-card__title">{blog.title}</h2>
          <p className="featured-card__excerpt">{blog.excerpt}</p>
          <div className="featured-card__meta">
            <AuthorAvatar name={blog.author} />
            <div>
              <span className="meta-name">{blog.author}</span>
              <span className="meta-role">{blog.authorRole}</span>
            </div>
            <div className="meta-right">
              <span className="meta-date"><FiCalendar /> {blog.date}</span>
              <span className="meta-time"><FiClock /> {blog.readTime}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Grid Card ────────────────────────────────────────────────────────────────

function BlogCard({ blog, onSelect, index = 0 }) {
  return (
    <article
      className="blog-card"
      onClick={() => onSelect(blog)}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="blog-card__img-wrap">
        <img src={blog.image} alt={blog.title} className="blog-card__img" />
        <CategoryChip category={blog.category} size="sm" />
      </div>
      <div className="blog-card__body">
        <h3 className="blog-card__title">{blog.title}</h3>
        <p className="blog-card__excerpt">{blog.excerpt}</p>
        <div className="blog-card__footer">
          <div className="blog-card__author">
            <AuthorAvatar name={blog.author} />
            <div>
              <span className="meta-name">{blog.author}</span>
              <span className="meta-role">{blog.authorRole}</span>
            </div>
          </div>
          <div className="blog-card__times">
            <span className="meta-date"><FiCalendar /> {blog.date}</span>
            <span className="meta-time"><FiClock /> {blog.readTime}</span>
          </div>
        </div>
      </div>
    </article>
  );
}

// ─── Stack Card (sidebar) ─────────────────────────────────────────────────────

function StackCard({ blog, onSelect }) {
  return (
    <div className="stack-card" onClick={() => onSelect(blog)}>
      <img src={blog.image} alt={blog.title} className="stack-card__img" />
      <div className="stack-card__body">
        <CategoryChip category={blog.category} size="xs" />
        <p className="stack-card__title">{blog.title}</p>
        <div className="stack-card__footer">
          <span className="meta-time"><FiClock /> {blog.readTime}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Detail View ──────────────────────────────────────────────────────────────

function BlogDetail({ blog, onBack }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div className="detail-overlay" onClick={onBack}>
      <div className="detail-panel" onClick={(e) => e.stopPropagation()}>
        <button className="detail-back" onClick={onBack}>
          <FiArrowLeft /> Back to all articles
        </button>

        <div className="detail-hero">
          <img src={blog.image} alt={blog.title} className="detail-hero__img" />
          <div className="detail-hero__gradient" />
          <div className="detail-hero__badge">
            <CategoryChip category={blog.category} size="md" />
          </div>
        </div>

        <div className="detail-body">
          <h1 className="detail-title">{blog.title}</h1>

          <div className="detail-meta">
            <AuthorAvatar name={blog.author} />
            <div>
              <strong className="meta-name">{blog.author}</strong>
              <span className="meta-role">{blog.authorRole}</span>
            </div>
            <div className="detail-meta__right">
              <span className="meta-date"><FiCalendar /> {blog.date}</span>
              <span className="meta-time"><FiClock /> {blog.readTime}</span>
            </div>
          </div>

          <div className="detail-content">
            {(blog.content || "").split("\n\n").map((para, i) => (
              <p key={i} className="detail-para">{para}</p>
            ))}
          </div>

          {blog.tags && blog.tags.length > 0 && (
            <div className="detail-tags">
              {blog.tags.map((tag) => (
                <span key={tag} className="tag"><FiTag /> {tag}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Hero Banner ──────────────────────────────────────────────────────────────

function HeroBanner({ total }) {
  return (
    <div className="hero-banner">
      <div className="hero-banner__noise" />
      <div className="hero-banner__content">
        <span className="hero-eyebrow"><FiBookOpen /> HR Insights Journal</span>
        <h1 className="hero-title">
          Smarter <em>People</em> Decisions<br />Start Here
        </h1>
        <p className="hero-sub">
          Expert perspectives on recruitment, compliance, culture &amp; employee
          experience — curated for modern HR leaders who move fast.
        </p>
        <div className="hero-stats">
          <div className="hero-stat">
            <span className="hero-stat__num">{total}+</span>
            <span className="hero-stat__label">Articles</span>
          </div>
          <div className="hero-stat__divider" />
          <div className="hero-stat">
            <span className="hero-stat__num">6</span>
            <span className="hero-stat__label">Categories</span>
          </div>
          <div className="hero-stat__divider" />
          <div className="hero-stat">
            <span className="hero-stat__num">12k</span>
            <span className="hero-stat__label">Monthly Readers</span>
          </div>
        </div>
      </div>
      <div className="hero-banner__deco">
        <div className="deco-ring deco-ring--1" />
        <div className="deco-ring deco-ring--2" />
        <div className="deco-ring deco-ring--3" />
      </div>
    </div>
  );
}

// ─── Main Blog Component ──────────────────────────────────────────────────────

export default function Blog() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBlog, setSelectedBlog] = useState(null);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/blogs`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const transformed = data.map((b) => ({
        ...b,
        image: b.image_url || b.image, // Properly maps your API's image_url
        author: b.author || "Anonymous",
        authorRole: b.authorRole || "Contributor",
        tags: Array.isArray(b.tags) ? b.tags : [], // Safety check for tags array
        featured: Boolean(b.featured) // Ensures it acts as a boolean
      }));

      setBlogs(transformed);
    } catch (err) {
      console.error("API failed:", err.message);
      setError("Unable to load articles at this time.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  const filteredBlogs = blogs.filter((b) => {
    const matchCat = activeCategory === "All" || b.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchSearch = !q
      || (b.title && b.title.toLowerCase().includes(q))
      || (b.excerpt && b.excerpt.toLowerCase().includes(q))
      || (b.tags && b.tags.some((t) => t.toLowerCase().includes(q)));
    return matchCat && matchSearch;
  });

  // Safe separation logic to prevent empty states when data is small
  const featuredBlog = activeCategory === "All" && !searchQuery
    ? filteredBlogs.find((b) => b.featured)
    : null;

  const nonFeaturedBlogs = featuredBlog
    ? filteredBlogs.filter((b) => b.id !== featuredBlog.id)
    : filteredBlogs;

  const sidebarBlogs = featuredBlog ? nonFeaturedBlogs.slice(0, 3) : [];
  const gridBlogs = featuredBlog ? nonFeaturedBlogs.slice(3) : nonFeaturedBlogs;

  // ── Loading ──
  if (loading) {
    return (
      <div className="blog-page blog-page--loading">
        <div className="spinner" />
        <p className="loading-text">Loading articles…</p>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="blog-page blog-page--error">
        <FiAlertTriangle className="error-icon" />
        <h2>Something went wrong</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={fetchBlogs}>
          <FiRefreshCw /> Try Again
        </button>
      </div>
    );
  }

  // ── Main ──
  return (
    <div className="blog-page">
      {/* Hero */}
      <div className="blog-page__hero">
        <HeroBanner total={blogs.length} />
      </div>

      {/* Toolbar */}
      <div className="blog-page__toolbar">
        <div className="toolbar">
          <div className="search-wrap">
            <FiSearch className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search articles by title, topic, or tag…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <nav className="cat-nav" aria-label="Blog categories">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`cat-btn${activeCategory === cat ? " cat-btn--active" : ""}`}
                onClick={() => setActiveCategory(cat)}
                aria-pressed={activeCategory === cat}
              >
                <span className="cat-btn__icon">{CAT_META[cat]?.icon}</span>
                {cat}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="blog-page__content">

        {filteredBlogs.length === 0 ? (
          <div className="empty-state">
            <FiBookOpen className="empty-state__icon" />
            <p className="empty-state__text">No articles found matching your criteria.</p>
          </div>
        ) : (
          <>
            {/* Featured Section */}
            {featuredBlog && (
              <section className="section section--featured">
                <div className="section__header">
                  <FiStar className="section__icon" />
                  <h2 className="section__title">Featured Article</h2>
                  <div className="section__line" />
                </div>
                <div className="featured-layout">
                  <FeaturedCard
                    blog={featuredBlog}
                    onSelect={setSelectedBlog}
                  />
                  {sidebarBlogs.length > 0 && (
                    <div className="featured-sidebar">
                      {sidebarBlogs.map((b) => (
                        <StackCard
                          key={b.id}
                          blog={b}
                          onSelect={setSelectedBlog}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Grid Section - Only show header if there are actually items in the grid */}
            {gridBlogs.length > 0 && (
              <section className="section section--grid">
                <div className="section__header">
                  <FiLayers className="section__icon" />
                  <h2 className="section__title">
                    {searchQuery
                      ? `Search Results (${filteredBlogs.length})`
                      : activeCategory === "All" && featuredBlog
                        ? "More Articles"
                        : activeCategory === "All"
                          ? "Latest Articles"
                          : activeCategory}
                  </h2>
                  <div className="section__line" />
                </div>

                <div className="blogs-grid">
                  {gridBlogs.map((blog, i) => (
                    <BlogCard
                      key={blog.id}
                      blog={blog}
                      index={i}
                      onSelect={setSelectedBlog}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {/* Detail Overlay */}
      {selectedBlog && (
        <BlogDetail
          blog={selectedBlog}
          onBack={() => setSelectedBlog(null)}
        />
      )}
    </div>
  );
}